import * as React from "react";
import intersection from "lodash-es/intersection";
import { getQuery } from "@stemscopes/api";
import classNames from "classnames";
import { endpoints, routes } from "configs";
import {
  useRequest,
  useLocation,
  useNavigate,
  useQueryParams,
  useTranslation,
} from "hooks";
import {
  Form,
  Icon,
  Input,
  Select,
  Dropdown,
  Checkbox,
  OptionGroup,
  Option,
  CheckboxGroup,
  CheckboxProvider,
  Typography,
} from "components";
import { Entities, Controls, Skeleton } from "./components";
import { FiltersTypes, filtersSelectors, filtersConstants } from "./duck";
import { getActivityIds, getCategoryIds } from "./duck/selectors";
import styles from "./Filters.module.css";

interface FiltersProps {
  className?: string;
  contentContainerClassName?: string;
}

const isOriginalLanguage = (localStorage.getItem("language") || "en") === "en";

const Filters: React.FC<FiltersProps> = ({
  className,
  contentContainerClassName,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useQueryParams();

  const [{ data: activityTypes }, fetchActivityTypes] = useRequest<
    FiltersTypes.ActivityType[]
  >({ url: endpoints.activityTypes });

  const [{ data: categoryTypes = [] }, fetchCategoryTypes] = useRequest<
    FiltersTypes.GroupedCaterogiesByTopic[]
  >({ url: endpoints.categoryTypes });

  const categoryIds = React.useMemo(
    () => getCategoryIds(categoryTypes),
    [categoryTypes],
  );

  const activityTypeIds = React.useMemo(
    () => getActivityIds(activityTypes),
    [activityTypes],
  );

  const [{ loading: loadingEntities, data: entitiesCount }, fetchCounts] =
    useRequest<FiltersTypes.EntityAssetsCount>({
      url: endpoints.countByEntity,
    });

  React.useEffect(() => {
    fetchActivityTypes().catch(() => {});
    fetchCategoryTypes().catch(() => {});
    fetchCounts().catch(() => {});
  }, []);

  const activities = React.useMemo(
    () => filtersSelectors.getActivities(activityTypes),
    [activityTypes],
  );

  const initialsFilters = React.useMemo(
    () =>
      filtersSelectors.getInitialFilters(params, entitiesCount, activityTypes),
    [activityTypes, entitiesCount, params],
  );

  React.useEffect(() => {
    const onMouseDown = (e: MouseEvent): void => {
      if (e.offsetX > (e.target as HTMLElement)?.clientWidth) {
        e.stopPropagation();
      }
    };
    window.addEventListener("mousedown", onMouseDown, true);

    return () => window.removeEventListener("mousedown", onMouseDown, true);
  }, []);

  const handleSubmit = async (values: any): Promise<void> => {
    if (location.pathname !== routes.streamingResources) {
      navigate({
        pathname: routes.streamingResources,
        search: getQuery(values),
      });
    } else {
      const page = params.page && { ...params.page, number: 1 };
      if (localStorage.getItem("language") !== "en") {
        setParams({
          ...params,
          ...values,
          page,
        });
      } else {
        setParams({ ...params, ...values, page });
      }
    }
  };

  return (
    <Dropdown
      size="small"
      position="right"
      variant="clear"
      label={t("streaming.searchForMedia")}
      wheelPropagation
      buttonClassName={styles.opener}
      contentClassName={styles.content}
      className={className}
      contentContainerClassName={classNames(
        styles.contentContainer,
        contentContainerClassName,
      )}
      prefix={<Icon name="search-outlined" size="small" />}
      suffix={(opened) => (
        <Icon size="small" name={opened ? "up-outlined" : "down-outlined"} />
      )}
    >
      {(onClose) => (
        <>
          <Typography component="h2" variant="h4">
            {t("streaming.filters")}
          </Typography>
          {loadingEntities ? (
            <Skeleton />
          ) : (
            <Form
              enableReinitialize
              className={styles.form}
              aria-label={t("streaming.streamingFilters")}
              initialValues={initialsFilters}
              onSubmit={handleSubmit}
            >
              {({ resetForm, values }) => (
                <>
                  <div className={styles.field}>
                    <Input
                      stretch
                      labelHidden
                      name="search"
                      label={t("streaming.search")}
                      placeholder={t("streaming.search")}
                      prefix={<Icon name="search-outlined" />}
                      type="search"
                      disabled={!isOriginalLanguage}
                    />
                    <Typography
                      component="p"
                      variant="c2"
                      className={styles.fieldDescription}
                    >
                      {t("streaming.searchBanner")}
                    </Typography>
                  </div>
                  <CheckboxProvider name="activities">
                    {({ indeterminate }) => {
                      const filters = activities.map((filter) => (
                        <Checkbox
                          key={filter.value}
                          label={filter.label}
                          value={filter.value}
                        />
                      ));

                      return (
                        <CheckboxGroup
                          size="small"
                          orientation="horizontal"
                          className={styles.field}
                          listClassName={classNames(
                            styles.fieldList,
                            styles.listStretch,
                          )}
                          label={
                            <Typography variant="label">
                              {t("streaming.searchIn")}
                            </Typography>
                          }
                          hiddenLabel={t("streaming.searchIn")}
                        >
                          <Checkbox
                            checkAll
                            label={t("streaming.selectAll")}
                            indeterminate={indeterminate}
                          />
                          {filters as any}
                        </CheckboxGroup>
                      );
                    }}
                  </CheckboxProvider>
                  <Entities
                    entitiesCount={entitiesCount}
                    listClassName={styles.fieldList}
                    labelClassName={styles.fieldLabel}
                    className={classNames(styles.field, styles.fieldGroup)}
                  />
                  <Select
                    multi
                    clearable
                    mode="autocomplete"
                    name="categories"
                    placeholder={t("streaming.selectSomeCategories")}
                    className={styles.field}
                    labelClassName={styles.fieldLabel}
                    label={
                      <Typography variant="label">
                        {t("streaming.categories")}
                      </Typography>
                    }
                    icon={<Icon name="times-light" />}
                    selectedIcon={<Icon name="check-light" />}
                    value={intersection(categoryIds, values.categories)}
                  >
                    {categoryTypes.map((topic) => (
                      <OptionGroup key={topic.topicId} label={topic.topicName}>
                        {topic.categories.map((category) => (
                          <Option key={category.id} value={category.id}>
                            {category.name}
                          </Option>
                        ))}
                      </OptionGroup>
                    ))}
                  </Select>
                  <Select
                    multi
                    clearable
                    mode="autocomplete"
                    name="activityTypes"
                    placeholder={t("streaming.selectSomeActivityTypes")}
                    data={activityTypes}
                    className={styles.field}
                    labelClassName={styles.fieldLabel}
                    icon={<Icon name="times-light" />}
                    selectedIcon={<Icon name="check-light" />}
                    label={
                      <Typography variant="label">
                        {t("streaming.hasActivity")}
                      </Typography>
                    }
                    renderItem={(activityType) => (
                      <Option key={activityType.id} value={activityType.id}>
                        {activityType.name}
                      </Option>
                    )}
                    value={intersection(activityTypeIds, values.activityTypes)}
                  />
                  <Controls
                    onClose={onClose}
                    onReset={() =>
                      resetForm({
                        values: filtersSelectors.getResettedFilters(
                          entitiesCount,
                          activityTypes,
                        ),
                      })
                    }
                    onClear={() =>
                      resetForm({ values: filtersConstants.EMPTY_FILTERS })
                    }
                  />
                </>
              )}
            </Form>
          )}
        </>
      )}
    </Dropdown>
  );
};

export default Filters;
